import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

function formatDate(dateStr, lang = 'en') {
  if (!dateStr) return ''
  const locale = lang === 'es' ? 'es-ES' : 'en-US'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })
    .formatToParts(new Date(dateStr + 'T00:00:00'))
    .map(({ type, value }) =>
      lang === 'es' && (type === 'weekday' || type === 'month')
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : value
    )
    .join('')
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0)
}

function Icon({ name, size = 20, className = '' }) {
  return (
    <span
      className={`material-symbols-outlined select-none leading-none ${className}`}
      style={{ fontSize: size }}
    >
      {name}
    </span>
  )
}

export default function Home() {
  const { user } = useAuth()
  const { t, i18n } = useTranslation()
  const [camps, setCamps] = useState([])
  const [userRegs, setUserRegs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      const { data: campsData } = await supabase
        .from('camps')
        .select('*, pricing_tiers(*)')
        .eq('registration_open', true)
        .order('created_at', { ascending: false })

      setCamps(campsData ?? [])

      if (user) {
        const { data: regsData } = await supabase
          .from('registrations')
          .select('camp_id')
          .eq('user_id', user.id)
          .not('payment_status', 'eq', 'Cancelled')

        setUserRegs(regsData ?? [])
      }

      setLoading(false)
    }
    fetchData()
  }, [user?.id])

  return (
    <div className="flex flex-col items-center">

      {/* ── Hero banner ───────────────────────────────────────────── */}
      <div className="w-full rounded-[2rem] overflow-hidden mb-10 shadow-xl">
        <img
          src="/banner-vasijas-de-barro.jpeg"
          alt={t('home.title')}
          className="w-full h-auto block"
        />
      </div>

      {/* ── Camp cards ────────────────────────────────────────────── */}
      <div className="w-full max-w-2xl space-y-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : camps.length > 0 ? (
          camps.map(camp => {
            const isRegistered = user && userRegs.some(r => r.camp_id === camp.id)
            const tiers = camp.pricing_tiers ?? []
            const defaultTier =
              tiers.find(tier => tier.is_default) ??
              [...tiers].sort((a, b) => a.price - b.price)[0] ??
              null
            const transportCost = parseFloat(camp.transport_cost ?? 0)

            return (
              <div
                key={camp.id}
                className="bg-surface-container-lowest rounded-[2rem] shadow-[0px_4px_24px_rgba(0,0,0,0.07)] overflow-hidden"
              >
                {/* Status stripe */}
                <div className="bg-primary px-6 py-3 flex items-center gap-2">
                  <Icon name="rocket_launch" size={16} className="text-on-primary" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-on-primary">
                    {t('home.openBadge')}
                  </span>
                </div>

                <div className="p-5 sm:p-8">
                  <h2 className="text-2xl font-bold text-on-surface">{camp.name}</h2>

                  {camp.description && (
                    <p className="mt-3 text-on-surface-variant leading-relaxed">
                      {camp.description}
                    </p>
                  )}

                  {/* Detail chips */}
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {camp.location && (
                      <div className="flex items-start gap-3 bg-surface-container-low p-4 rounded-2xl">
                        <div className="w-10 h-10 bg-surface-container-high rounded-xl flex items-center justify-center shrink-0">
                          <Icon name="location_on" size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                            {t('home.location')}
                          </p>
                          <p className="text-sm font-medium text-on-surface mt-0.5">
                            {camp.location}
                          </p>
                        </div>
                      </div>
                    )}

                    {camp.start_date && (
                      <div className="flex items-start gap-3 bg-surface-container-low p-4 rounded-2xl">
                        <div className="w-10 h-10 bg-surface-container-high rounded-xl flex items-center justify-center shrink-0">
                          <Icon name="event" size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                            {t('home.startDate')}
                          </p>
                          <p className="text-sm font-medium text-on-surface mt-0.5">
                            {formatDate(camp.start_date, i18n.language)}
                          </p>
                        </div>
                      </div>
                    )}

                    {camp.end_date && (
                      <div className="flex items-start gap-3 bg-surface-container-low p-4 rounded-2xl">
                        <div className="w-10 h-10 bg-surface-container-high rounded-xl flex items-center justify-center shrink-0">
                          <Icon name="flag" size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                            {t('home.endDate')}
                          </p>
                          <p className="text-sm font-medium text-on-surface mt-0.5">
                            {formatDate(camp.end_date, i18n.language)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pricing chips */}
                  <div className="mt-4 flex flex-wrap gap-3">
                    <div className="flex items-center gap-3 bg-surface-container-low p-4 rounded-2xl">
                      <div className="w-10 h-10 bg-surface-container-high rounded-xl flex items-center justify-center shrink-0">
                        <Icon name="payments" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                          {t('home.campFee')}
                        </p>
                        <p className="text-sm font-bold text-primary mt-0.5">
                          {defaultTier ? formatCurrency(defaultTier.price) : t('home.feeTbd')}
                        </p>
                      </div>
                    </div>

                    {transportCost > 0 && (
                      <div className="flex items-center gap-3 bg-surface-container-low p-4 rounded-2xl">
                        <div className="w-10 h-10 bg-surface-container-high rounded-xl flex items-center justify-center shrink-0">
                          <Icon name="directions_bus" size={20} className="text-secondary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                            {t('home.transport')}
                          </p>
                          <p className="text-sm font-bold text-secondary mt-0.5">
                            {formatCurrency(transportCost)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="mt-8">
                    {isRegistered ? (
                      <Link
                        to="/my-registration"
                        className="flex items-center justify-center gap-2 w-full h-14 rounded-2xl bg-primary text-on-primary font-semibold text-base hover:opacity-90 active:scale-[0.98] transition shadow-sm"
                      >
                        {t('home.viewMyRegistration')}
                        <Icon name="arrow_forward" size={20} className="text-on-primary" />
                      </Link>
                    ) : user ? (
                      <Link
                        to={`/register-camp?campId=${camp.id}`}
                        className="flex items-center justify-center gap-2 w-full h-14 rounded-2xl bg-primary text-on-primary font-semibold text-base hover:opacity-90 active:scale-[0.98] transition shadow-sm"
                      >
                        {t('home.registerForCamp')}
                        <Icon name="arrow_forward" size={20} className="text-on-primary" />
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/register"
                          className="flex items-center justify-center gap-2 w-full h-14 rounded-2xl bg-primary text-on-primary font-semibold text-base hover:opacity-90 active:scale-[0.98] transition shadow-sm"
                        >
                          {t('home.registerForCamp')}
                          <Icon name="arrow_forward" size={20} className="text-on-primary" />
                        </Link>
                        <p className="mt-3 text-center text-xs text-on-surface-variant">
                          {t('home.alreadyRegistered')}{' '}
                          <Link to="/login" className="text-primary hover:underline font-medium">
                            {t('home.signIn')}
                          </Link>{' '}
                          {t('home.toViewRegistration')}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          /* No active camps */
          <div className="bg-surface-container-lowest rounded-[2rem] shadow-[0px_4px_24px_rgba(0,0,0,0.07)] p-8 sm:p-12 text-center">
            <div className="w-16 h-16 bg-surface-container-high rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="forest" size={32} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-on-surface">
              {t('home.noCampsTitle')}
            </h2>
            <p className="mt-2 text-sm text-on-surface-variant max-w-sm mx-auto">
              {t('home.noCampsSubtitle')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
